import type { GrowlAction } from '../ui-feedback/growl.service';
import { realtimeTypes } from '../realtime/realtime-message';
import {
  SavedUserActionNotification, SavedUserActionNotificationOf, SavedUserActionType, SystemNotification,
  SystemNotificationOf, SystemNotificationType, readSavedUserActionOfType, userActionTypes
} from './notification-messages';

export interface NotificationGrowl {
  notificationType: string;
  clubId: string | null;
  dedupeKey: string;
  message: string;
  action?: GrowlAction;
}

interface GrowlHandler<T> {
  type: string;
  describe(notification: T): NotificationGrowl | undefined;
}

// A registered handler receives only its declared typed payload. The single narrowing cast is
// isolated in the registration helper, not repeated by the UI/orchestrator.
function systemHandler<T extends SystemNotificationType>(
  type: T, describe: (event: SystemNotificationOf<T>) => NotificationGrowl | undefined
): GrowlHandler<SystemNotification> {
  return {
    type,
    describe: event => event.type === type ? describe(event as SystemNotificationOf<T>) : undefined
  };
}

function savedActionHandler<T extends SavedUserActionType>(
  type: T, describe: (event: SavedUserActionNotificationOf<T>) => NotificationGrowl | undefined
): GrowlHandler<SavedUserActionNotification> {
  return {
    type,
    describe: notification => {
      const typed = readSavedUserActionOfType(notification, type);
      return typed ? describe(typed) : undefined;
    }
  };
}

// These notifications are *invalidation hints*. Descriptions must not claim that a register
// was closed, an invitation accepted, or any payment succeeded without a verified saved read.
const systemHandlers: readonly GrowlHandler<SystemNotification>[] = [
  systemHandler(realtimeTypes.classRegisterChanged, event => ({
    notificationType: event.type,
    clubId: event.payload.clubId,
    dedupeKey: `register:${event.payload.clubId}:${event.payload.sessionId}`,
    message: 'A class register has been updated. Open it to see the latest attendance.',
    action: {
      label: 'View register',
      route: ['/dashboard/clubs', event.payload.clubId, 'sessions', event.payload.sessionId]
    }
  })),
  systemHandler(realtimeTypes.attendanceExpectationUpdated, event => ({
    notificationType: event.type,
    clubId: event.payload.clubId,
    dedupeKey: `attendance-expectation:${event.payload.clubId}:${event.payload.sessionId}`,
    message: 'An absence or late-arrival report may have changed. Open the session to check.',
    action: {
      label: 'View session',
      route: ['/dashboard/clubs', event.payload.clubId, 'sessions', event.payload.sessionId]
    }
  })),
  systemHandler(realtimeTypes.registrationUpdated, event => ({
    notificationType: event.type,
    clubId: event.payload.clubId,
    dedupeKey: `registrations:${event.payload.clubId}:${event.payload.termId}`,
    message: 'Registration updates are available. Open registrations to see the latest details.',
    action: {
      label: 'View registrations',
      route: ['/dashboard/clubs', event.payload.clubId, 'terms', event.payload.termId, 'registrations']
    }
  })),
  systemHandler(realtimeTypes.termEmailInvitationChanged, event => ({
    notificationType: event.type,
    clubId: event.payload.clubId,
    dedupeKey: `term-invitations:${event.payload.clubId}:${event.payload.termId}`,
    message: 'A term invitation has changed. Open the term to check the details.',
    action: {
      label: 'View term',
      route: ['/dashboard/clubs', event.payload.clubId, 'terms', event.payload.termId]
    }
  }))
];

// A new-registration SignalR hint is deliberately not registered: the corresponding growl
// comes from the verified, persisted UserAction instead. Payment/provider hints are also
// deliberately omitted until an authoritative read confirms the outcome.
const savedActionGrowlWindowMs = 15 * 60 * 1000;

const savedActionHandlers: readonly GrowlHandler<SavedUserActionNotification>[] = [
  savedActionHandler(userActionTypes.newRegistrationForClubStaff, event => {
    const createdAt = Date.parse(event.action.createdAt);

    // Old actions remain visible in the persistent inbox; they should not raise new toasts
    // when a delayed refresh discovers them after the event is no longer timely.
    if (!Number.isFinite(createdAt) || Math.abs(Date.now() - createdAt) > savedActionGrowlWindowMs)
      return undefined;

    const clubName = typeof event.action.clubName === 'string' ? event.action.clubName.trim() : '';

    return {
      notificationType: event.type,
      clubId: event.payload.clubId,
      dedupeKey: `saved-action:${event.action.id.toLowerCase()}`,
      message: clubName
        ? `A new registration has been submitted at ${clubName}.`
        : 'A new club registration has been submitted.',
      action: {
        label: 'View registrations',
        route: ['/dashboard/clubs', event.payload.clubId, 'terms', event.payload.termId, 'registrations']
      }
    };
  })
];

const systemRegistry = new Map(systemHandlers.map(handler => [handler.type, handler]));
const savedActionRegistry = new Map(savedActionHandlers.map(handler => [handler.type, handler]));

export function describeSystemGrowl(event: SystemNotification): NotificationGrowl | undefined {
  return systemRegistry.get(event.type)?.describe(event);
}

export function describeSavedActionGrowl(event: SavedUserActionNotification): NotificationGrowl | undefined {
  return savedActionRegistry.get(event.type)?.describe(event);
}
